import * as React from 'react'
import { useIntl } from 'react-intl'
import dayjs from "dayjs";
import Box from '@mui/material/Box'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableContainer from '@mui/material/TableContainer'
import TableFooter from '@mui/material/TableFooter'
import TablePagination from '@mui/material/TablePagination'
import TableRow from '@mui/material/TableRow'
import Paper from '@mui/material/Paper'
import { TableHead } from '@mui/material'
import TablePaginationActions from '../../../../components/muiComponents/tablePaginationActions/TablePaginationActions'
import { SalesAnalysisState } from '../../../../features/types'
import { capitalize } from '../../../../utils/functions'

interface Props {
    analyzes: SalesAnalysisState[]
    pageNumber: number
    totalCount: number
    page: number
    rowsPerPage: number
    totalPages: number
    isFetching: boolean
    setPage: (page: number) => void
    setRowsPerPage: (rowsPerPage: number) => void
}

const SalesReportTable: React.FC<Props> = ({
    analyzes = [],
    pageNumber = 0,
    totalCount = 0,
    page = 0,
    rowsPerPage,
    isFetching,
    setPage,
    setRowsPerPage,
}) => {
    const { formatMessage, locale } = useIntl()

    const emptyRows = page > 0 ? Math.max(0, rowsPerPage - analyzes.length) : 0

    const handleChangePage = (
        event: React.MouseEvent<HTMLButtonElement> | null,
        newPage: number,
    ) => {
        setPage(newPage + 1)
    }

    const handleChangeRowsPerPage = (
        event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    ) => {
        setRowsPerPage(parseInt(event.target.value, 10))
        setPage(1)
    }

    return (
        <Box>
            <TableContainer
                component={Paper}
                sx={{ border: 1, borderColor: 'grey.100', borderRadius: 2 }}
                elevation={0}
            >
                <Table
                    sx={{ minWidth: 500 }}
                    aria-label="custom pagination table"
                >
                    <TableHead sx={{ bgcolor: 'grey.100' }}>
                        <TableRow>
                            <TableCell>#</TableCell>

                            <TableCell align="right">
                                invoiceId
                            </TableCell>

                            <TableCell align="center">
                                txnId
                            </TableCell>

                            <TableCell align="center">
                                txnDate
                            </TableCell>

                            <TableCell>
                                {formatMessage({ id: 'registeredTime' })}
                            </TableCell>

                            <TableCell>
                                {formatMessage({ id: 'phone' })}
                            </TableCell>

                            <TableCell>
                                {formatMessage({ id: 'fullName' })}
                            </TableCell>

                            <TableCell>Курсы</TableCell>

                            <TableCell align="right">
                                {formatMessage({
                                    id: 'registeredSubject',
                                })}
                            </TableCell>

                            <TableCell align="right">
                                {formatMessage({
                                    id: 'topicNumber',
                                })}
                            </TableCell>

                            <TableCell align="right">
                                {formatMessage({
                                    id: 'totalSum',
                                })}
                            </TableCell>
                        </TableRow>
                    </TableHead>

                    {analyzes.length ? (
                        <TableBody>
                            {analyzes.map((row, index) => (
                                <TableRow key={row.invoiceId}>
                                    <TableCell
                                        component="th"
                                        scope="row"
                                    >
                                        {(page - 1) * rowsPerPage + index + 1}
                                    </TableCell>

                                    <TableCell align="right">
                                        {row.invoiceId}
                                    </TableCell>

                                    <TableCell align="right">
                                        {row?.txnId}
                                    </TableCell>

                                    <TableCell align="right">
                                        {row?.txtDate && dayjs(row?.txtDate).format("DD.MM.YYYY HH:mm")}
                                    </TableCell>

                                    <TableCell
                                        component="th"
                                        scope="row"
                                    >
                                        {row?.dateTime && dayjs(row?.dateTime).format("DD.MM.YYYY HH:mm")}
                                    </TableCell>

                                    <TableCell
                                        component="th"
                                        scope="row"
                                    >
                                        {row.mobile}
                                    </TableCell>

                                    <TableCell>{row.fullName}</TableCell>

                                    <TableCell>
                                        {row[`title${capitalize(locale)}`]}
                                    </TableCell>

                                    <TableCell align="right">
                                        {row.subjectCount}
                                    </TableCell>

                                    <TableCell align="right">
                                        {row.topicCount}
                                    </TableCell>

                                    <TableCell align="right">
                                        {row?.amount?.toLocaleString()} т
                                    </TableCell>
                                </TableRow>
                            ))}
                            {emptyRows > 0 && (
                                <TableRow style={{ height: 53 * emptyRows }}>
                                    <TableCell colSpan={11} />
                                </TableRow>
                            )}
                        </TableBody>
                    ) : (
                        !isFetching && (
                            <TableBody>
                                <TableRow>
                                    <TableCell
                                        component="th"
                                        scope="row"
                                        align="center"
                                        colSpan={11}
                                    >
                                        {formatMessage({ id: 'noData' })}
                                    </TableCell>
                                </TableRow>
                            </TableBody>
                        )
                    )}

                    <TableFooter>
                        <TableRow>
                            <TablePagination
                                rowsPerPageOptions={[
                                    5,
                                    10,
                                    25,
                                    { label: 'All', value: -1 },
                                ]}
                                colSpan={11}
                                count={totalCount}
                                rowsPerPage={rowsPerPage}
                                page={pageNumber}
                                SelectProps={{
                                    inputProps: {
                                        'aria-label': 'rows per page',
                                    },
                                    native: true,
                                }}
                                onPageChange={handleChangePage}
                                onRowsPerPageChange={handleChangeRowsPerPage}
                                ActionsComponent={TablePaginationActions}
                                sx={{ border: 0 }}
                            />
                        </TableRow>
                    </TableFooter>
                </Table>
            </TableContainer>
        </Box>
    )
}

export default SalesReportTable
